import React from "react";
import { Banner, Button, FormField, Input, Select } from "@/ds";
import { ApiError, cockpit } from "@/api";
import type { OwnerRuleValueDelegation, OwnerTenantRuleParameters, RuleDelegationIssue, RuleParamKey, RuleValueDelegation } from "@/api";
import { useLang, useT } from "../i18n";
import type { PushToast } from "@/notifications/toast-queue";

import { ruleValueNumber, parseRuleValueInput } from "./rule-parameter-display";

type GrantDraft = { editable: boolean; min: string; max: string; step: string };
type Draft = Record<RuleParamKey, GrantDraft>;
const RANGE_FIELDS = ["min", "max", "step"] as const;

function draftOf(data: OwnerRuleValueDelegation, parameters: OwnerTenantRuleParameters["parameters"]): Draft {
  return Object.fromEntries(parameters.map((definition) => {
    const grant = data.keys[definition.key];
    return [definition.key, {
      editable: grant?.editable === true,
      min: String(ruleValueNumber(grant?.editable ? grant.min ?? definition.min_inclusive ?? 0 : definition.min_inclusive ?? 0, definition.unit)),
      max: String(ruleValueNumber(grant?.editable ? grant.max ?? definition.max_inclusive ?? 0 : definition.max_inclusive ?? 0, definition.unit)),
      step: String(ruleValueNumber(grant?.editable ? grant.step ?? definition.step ?? 1 : definition.step ?? 1, definition.unit)),
    }];
  })) as Draft;
}

// Convert form text only; the server decides whether the grant and its range are valid.
function keysOf(draft: Draft, parameters: OwnerTenantRuleParameters["parameters"]): RuleValueDelegation {
  return Object.fromEntries(parameters.flatMap<[RuleParamKey, unknown]>((definition) => {
    const field = draft[definition.key];
    if (!field.editable) return [];
    return [[definition.key, definition.value_type === "boolean"
      ? { editable: true }
      : { editable: true, min: parseRuleValueInput(field.min, definition.unit), max: parseRuleValueInput(field.max, definition.unit), step: parseRuleValueInput(field.step, definition.unit) }]];
  })) as RuleValueDelegation;
}

export function TenantRuleDelegation({ tenantId, values, data, onSaved, reload, refreshValues, pushToast, remoteChanged, onEditStateChange }: {
  tenantId: string;
  values: OwnerTenantRuleParameters;
  data: OwnerRuleValueDelegation;
  onSaved: (data: OwnerRuleValueDelegation) => void;
  reload: () => Promise<void>;
  refreshValues: () => void;
  pushToast: PushToast;
  remoteChanged: boolean;
  onEditStateChange: (dirty: boolean, busy: boolean) => void;
}) {
  const t = useT();
  const { lang } = useLang();
  const [draft, setDraft] = React.useState(() => draftOf(data, values.parameters));
  const [issues, setIssues] = React.useState<RuleDelegationIssue[]>([]);
  const [conflict, setConflict] = React.useState<number | null>(null);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [blocked, setBlocked] = React.useState(false);
  React.useEffect(() => {
    setDraft(draftOf(data, values.parameters));
    setIssues([]);
    setConflict(null);
    setSaveError(null);
    setBlocked(false);
  // Only a new saved delegation replaces its draft; saving values must not discard it.
  }, [data]);

  const keys = keysOf(draft, values.parameters);
  const baseline = keysOf(draftOf(data, values.parameters), values.parameters);
  const dirty = JSON.stringify(keys) !== JSON.stringify(baseline);
  React.useEffect(() => { onEditStateChange(dirty, busy); }, [dirty, busy, onEditStateChange]);
  React.useEffect(() => () => { onEditStateChange(false, false); }, [onEditStateChange]);
  const readOnly = !values.editable || blocked;
  const edit = (key: RuleParamKey, patch: Partial<GrantDraft>) => {
    setDraft((previous) => ({ ...previous, [key]: { ...previous[key], ...patch } }));
    setIssues((previous) => previous.filter((issue) => issue.key !== key));
  };
  const discard = () => {
    if (conflict !== null) { void reload(); return; }
    setDraft(draftOf(data, values.parameters));
    setIssues([]);
    setSaveError(null);
  };
  const save = async () => {
    if (remoteChanged) return;
    setBusy(true);
    setIssues([]);
    setSaveError(null);
    try {
      const next = await cockpit.updateTenantRuleDelegation(tenantId, { expected_version: data.version, keys });
      onSaved(next);
      pushToast({ tone: "success", title: t("businesses.rules.delegation.saved", { version: next.version }) });
    } catch (error) {
      const body = error instanceof ApiError ? error.data as Record<string, unknown> | undefined : undefined;
      if (body?.code === "invalid_rule_delegation" && Array.isArray(body.issues)) {
        setIssues(body.issues as RuleDelegationIssue[]);
      } else if (body?.code === "delegation_excludes_active_value") {
        setIssues([{ code: String(body.code), key: String(body.key), reason: String(body.reason) }]);
      } else if (body?.code === "rule_delegation_changed") {
        setConflict(typeof body.current_version === "number" ? body.current_version : data.version);
      } else if (body?.code === "tenant_read_only") {
        setBlocked(true);
        setSaveError("tenant_read_only");
        refreshValues();
      } else {
        setSaveError(String(body?.code ?? (error as Error)?.message ?? "unknown"));
        if (body?.code === "tenant_rule_delegation_invalid" || body?.code === "tenant_rule_parameter_set_invalid") setBlocked(true);
      }
    } finally { setBusy(false); }
  };
  const issueText = (issue: RuleDelegationIssue) => t(`businesses.rules.delegation.issue.${issue.code}`, undefined,
    t("businesses.rules.issue.other", { code: issue.code }));

  return <div className="stack-sm">
    <p className="subtle reqventos-rule-parameters__intro">{t("businesses.rules.delegation.hint")}</p>
    <p className="subtle">{t("businesses.rules.delegation.version", { version: data.version })}</p>
    {conflict !== null && <Banner tone="warning" title={t("businesses.rules.delegation.conflict")}>
      <p>{t("businesses.rules.conflict.body", { version: conflict })}</p>
      <Button variant="secondary" size="sm" onClick={() => { void reload(); }}>{t("businesses.rules.delegation.reload")}</Button>
    </Banner>}
    {issues.length > 0 && <Banner tone="danger" title={t("businesses.rules.invalid.title")}>
      {t("businesses.rules.invalid.body")}
      {issues.filter((issue) => !values.parameters.some((parameter) => parameter.key === issue.key))
        .map((issue, index) => <p key={index}>{issueText(issue)}</p>)}
    </Banner>}
    {saveError && <Banner tone="danger" title={t("businesses.rules.invalid.title")}>
      {saveError === "tenant_read_only" ? t("businesses.rules.readOnly.body") : t("businesses.rules.issue.other", { code: saveError })}
    </Banner>}
    {values.parameters.map((definition) => {
      const key = definition.key;
      const field = draft[key];
      const errors = issues.filter((issue) => issue.key === key).map(issueText).join(" ") || undefined;
      const numeric = definition.value_type === "integer";
      const technical = numeric
        ? t("businesses.rules.delegation.technical", { min: ruleValueNumber(definition.min_inclusive ?? 0, definition.unit), max: ruleValueNumber(definition.max_inclusive ?? 0, definition.unit), step: ruleValueNumber(definition.step ?? 1, definition.unit) })
        : t("businesses.rules.delegation.boolean");
      return <div className="reqventos-rule-parameter stack-sm" key={key}>
        <FormField label={t(`businesses.rules.param.${key}`, undefined, key)} htmlFor={`delegation-${key}`}
          helper={`${definition.description[lang] ?? definition.description.en} ${technical}`}
          error={!numeric || !field.editable ? errors : undefined}>
          <Select id={`delegation-${key}`} value={field.editable ? "business" : "platform"} disabled={busy || readOnly}
            options={[{ value: "platform", label: t("businesses.rules.plane.platform") }, { value: "business", label: t("businesses.rules.plane.business") }]}
            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => edit(key, { editable: event.target.value === "business" })} />
        </FormField>
        {numeric && field.editable && <div className="reqventos-rule-delegation__range">
          {RANGE_FIELDS.map((name) => <FormField key={name} label={t(`businesses.rules.delegation.${name}`)} htmlFor={`delegation-${key}-${name}`} error={errors}>
            <Input id={`delegation-${key}-${name}`} type="number" inputMode={definition.unit === "eur_cents" ? "decimal" : "numeric"} step={ruleValueNumber(definition.step ?? 1, definition.unit)} value={field[name]} disabled={busy || readOnly}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => edit(key, { [name]: event.target.value })} />
          </FormField>)}
        </div>}
      </div>;
    })}
    {!readOnly && <div className="management-actions">
      {dirty && <span className="subtle reqventos-rule-parameters__unsaved">{t("businesses.rules.unsaved")}</span>}
      <Button variant="ghost" disabled={busy || !dirty} onClick={discard}>{t("businesses.rules.discard")}</Button>
      <Button variant="primary" loading={busy} disabled={busy || !dirty || conflict !== null || remoteChanged} onClick={() => { void save(); }}>
        {t("businesses.rules.delegation.save")}
      </Button>
    </div>}
  </div>;
}
